/**
 * featureRegistry.js — single source of truth for discoverable features.
 * Powers the homepage "New to explore" band and the header search.
 * Consistent card data everywhere: name, one-line desc, time, age tier,
 * "what it builds", icon + synonyms for search.
 */
import { track } from "@/lib/track";
import { COUNTRIES_LABEL } from "@/lib/siteFacts";

const OPENS_KEY = "mwq_feature_opens_v1";
const TIERS = ["seedlings", "explorers", "pioneers"];   // 4–6 / 7–10 / 11–15
const NEW_WINDOW_MS = 14 * 24 * 3600 * 1000;

// addedAt drives the time-limited "New" badge (expires after 14 days or on first open).
const RECENT = Date.parse("2026-06-01");

export const FEATURES = [
  { key: "learn-ai", name: "AI Quest", emoji: "🤖", path: "/learn/ai",
    desc: "Learn how AI works, and use it safely", minutes: "5-10 min", builds: "AI literacy, data & ethics",
    tiers: ["explorers", "pioneers"], addedAt: RECENT,
    synonyms: ["ai", "artificial intelligence", "machine learning", "ai island", "teach the machine", "pattern trainer", "prompt", "prompt builder", "fair or unfair", "bias", "data", "ai literacy", "training data", "generative ai", "robot", "algorithm"] },
  { key: "english-quest", name: "English Quest", emoji: "🗣️", path: "/english-assessment",
    desc: "Find out what you can do in English", minutes: "10 min", builds: "Reading, listening & writing",
    tiers: ["seedlings", "explorers", "pioneers"], addedAt: RECENT,
    synonyms: ["english", "english level", "english test", "cefr", "reading level", "assessment", "placement"] },
  { key: "times-tables-quest", name: "Times Tables Quest", emoji: "✖️", path: "/times-tables-quest",
    desc: "Get quick at multiplication & division", minutes: "5 min", builds: "Times tables",
    tiers: ["explorers", "pioneers"], addedAt: RECENT,
    synonyms: ["times tables", "multiplication", "division", "maths facts", "fluency", "ttrs", "tables"] },
  { key: "language-quest", name: "Language Quest", emoji: "🔤", path: "/language-quest",
    desc: "Playful word games", minutes: "5 min", builds: "Vocabulary & spelling",
    tiers: ["seedlings", "explorers", "pioneers"], addedAt: Date.parse("2026-02-01"),
    synonyms: ["words", "spelling", "vocabulary", "language", "word games"] },
  { key: "quick-rounds", name: "Quick Rounds", emoji: "⚡", path: "/quiz",
    desc: "Fast, fun quiz rounds", minutes: "3 min", builds: "General knowledge",
    tiers: ["seedlings", "explorers", "pioneers"], addedAt: Date.parse("2026-01-01"),
    synonyms: ["quiz", "quick quiz", "rounds", "trivia"] },
  { key: "daily-world-quest", name: "Daily World Quest", emoji: "🌍", path: "/",
    desc: "Today's adventure around the world", minutes: "5 min", builds: "A bit of everything",
    tiers: ["seedlings", "explorers", "pioneers"], addedAt: Date.parse("2026-01-01"),
    synonyms: ["daily", "today", "world quest", "daily quest"] },
  { key: "concept-games", name: "Concept Games", emoji: "🎮", path: "/games",
    desc: "Learning games arcade", minutes: "5 min", builds: "Thinking & problem-solving",
    tiers: ["seedlings", "explorers", "pioneers"], addedAt: Date.parse("2026-01-01"),
    synonyms: ["games", "arcade", "play", "concept"] },
  { key: "code-quest", name: "Code Quest", emoji: "💻", path: "/learn/code-quest",
    desc: "Learn to code step by step", minutes: "10 min", builds: "Coding & logic",
    tiers: ["explorers", "pioneers"], addedAt: Date.parse("2026-03-01"),
    synonyms: ["code", "coding", "programming", "computer science", "algorithm", "blocks"] },
  { key: "globe", name: "Explore the Globe", emoji: "🌍", path: "/globe",
    desc: `Travel the world and ${COUNTRIES_LABEL}`, minutes: "5 min", builds: "Geography & cultures",
    tiers: ["seedlings", "explorers", "pioneers"], addedAt: Date.parse("2026-01-01"),
    synonyms: ["globe", "countries", "geography", "world", "map", "continents", "flags"] },
];

export const FEATURE_BY_KEY = Object.fromEntries(FEATURES.map((f) => [f.key, f]));

function _opens() { try { return JSON.parse(localStorage.getItem(OPENS_KEY) || "{}"); } catch { return {}; } }
export function isOpened(key) { return !!_opens()[key]; }
export function markOpened(key) {
  const o = _opens();
  if (!o[key]) { o[key] = Date.now(); try { localStorage.setItem(OPENS_KEY, JSON.stringify(o)); } catch { /* ignore */ }
    track("feature_first_open", { feature: key }); }
}
export function isNew(f) { return !isOpened(f.key) && (Date.now() - (f.addedAt || 0) < NEW_WINDOW_MS); }

/** Age suitability: exact tier match, or "for older explorers" if it's above the child's tier. */
export function suitability(f, tier) {
  if (!tier || f.tiers.includes(tier)) return "ok";
  const minIdx = Math.min(...f.tiers.map((t) => TIERS.indexOf(t)));
  return TIERS.indexOf(tier) < minIdx ? "older" : "ok";
}

/** Band ordering: unopened + age-appropriate first; fall back to opened (jump back in). */
export function bandFeatures(tier) {
  const withMeta = FEATURES.map((f) => ({ ...f, opened: isOpened(f.key), fit: suitability(f, tier) }));
  const unopened = withMeta.filter((f) => !f.opened && f.fit === "ok");
  const older = withMeta.filter((f) => !f.opened && f.fit === "older");
  const opened = withMeta.filter((f) => f.opened);
  const primary = [...unopened, ...older];
  if (primary.length) return { mode: "new", items: primary.slice(0, 4) };
  if (opened.length) return { mode: "jump", items: opened.slice(0, 4) };
  return { mode: "empty", items: [] };
}

export function searchFeatures(q) {
  const s = q.trim().toLowerCase();
  if (!s) return [];
  return FEATURES.filter((f) =>
    f.name.toLowerCase().includes(s) ||
    f.desc.toLowerCase().includes(s) ||
    f.builds.toLowerCase().includes(s) ||
    (f.synonyms || []).some((syn) => syn.includes(s) || s.includes(syn)),
  );
}
